<template>
    <h5 class="link-header geist-font mx-4 mb-4" id="products">Mapped Products</h5>
    <div class="patent-mapped-products d-flex w-full px-4 mb-4 flex-wrap">
        <div class="run-sim-button flex-grow-1">
            <div class="card-content h-full w-full">
                <div
                    v-if="!loadingProducts && productList.length === 0 && !noProducts"
                    class="cta h-full d-flex align-items-center justify-content-center flex-wrap"
                >
                    <p class="geist-font large semi-bold w-full text-center">
                        Where does this patent fit in the product landscape?
                    </p>
                    <AonButton
                        class="mt-n8"
                        label="Run Product Mapping"
                        @clicked="onRunProductMapping"
                    />
                </div>
                <div
                    v-if="!loadingProducts && noProducts"
                    class="no-patents h-full d-flex align-items-center justify-content-center flex-wrap"
                >
                    <p class="geist-font large semi-bold w-full text-center">
                        No product identified
                    </p>
                </div>
                <AonCoverLoading :loading="loadingProducts" title="Mapping Products..." />
            </div>
        </div>
        <AonCard v-if="productList.length > 1" class="w-full mb-2">
            <div class="chart-holder">
                <div id="productMapChart"></div>
            </div>
            <div class="legend d-flex align-items-center"></div>
        </AonCard>
    </div>
</template>

<script setup>
import { ref, inject, watch, nextTick, onMounted, onBeforeUnmount, defineExpose } from 'vue'
import { runProductMapping } from '@/api/patentViewer'
import { useEntityStore, usePatentViewerStore } from '@/stores'

import * as am5 from '@amcharts/amcharts5'
import { useSimpleRangeBulletChart } from '@/composables/charts/simpleRangeBulletChart'
import { config } from '@/config'
import { debounce } from 'lodash-es'

const logger = inject('logger')
const filters = inject('filters')
const eventBus = inject('eventBus')

const entityStore = useEntityStore()
const patentViewerStore = usePatentViewerStore()

const props = defineProps({
    patentDetails: {
        type: Object,
        default: () => ({}),
    },
    loadingPatentDetail: {
        type: Boolean,
        default: false,
    },
})

const loadingProducts = ref(false)
const productList = ref([])
const noProducts = ref(false)
const chartData = ref(null)
const currentPatentId = ref(null)

let root
let simpleRangeBulletChart

onMounted(() => {})

onBeforeUnmount(() => {
    if (root) {
        root.dispose()
        root = null
    }
})

// Reset component state
const resetComponentState = () => {
    productList.value = []
    noProducts.value = false
    chartData.value = null

    // Clean up chart
    if (root) {
        root.dispose()
        root = null
    }
}

const onRunProductMapping = async () => {
    const pubPk = props.patentDetails.publicationPk
    const entityPk = entityStore.entity.aon_entity_pk

    loadingProducts.value = true
    try {
        const { data } = await runProductMapping(pubPk, entityPk)
        productList.value = data
        console.log(data)

        if (productList.value.length === 0) {
            noProducts.value = true
            return
        }
    } catch (err) {
        logger.error(err)
        eventBus?.emit?.('snacktime', {
            type: 'error',
            message: 'Failed to map products from patent',
            contextual: true,
        })
        noProducts.value = true
    } finally {
        // if (productList.value.length > 1) {
        //     mapDataToChart()
        // }
        loadingProducts.value = false
    }
}

const mapDataToChart = () => {
    if (productList.value.length <= 0) return

    nextTick(() => {
        initializeChart()
    })
}

const initializeChart = async () => {
    if (root) {
        root.dispose()
        root = null
    }

    await nextTick()

    // Only initialize chart if the component is still mounted and we have data
    if (!chartData.value || chartData.value.length === 0) return

    try {
        am5.addLicense(config.license.AMChartsLicense)
        root = am5.Root.new('productMapChart')

        simpleRangeBulletChart = useSimpleRangeBulletChart(root)

        simpleRangeBulletChart.configureSimpleRangeBullet()
        simpleRangeBulletChart.generateAxes(chartData.value)
        simpleRangeBulletChart.generateSeries(chartData.value)
    } catch (err) {
        logger.error('Error initializing chart:', err)
    }
}

defineExpose({
    onRunProductMapping,
    productList,
    loadingProducts,
    noProducts,
})
</script>

<style scoped lang="scss">
@forward '@/styles/new_styles.scss';

.patent-mapped-products {
    min-height: 200px;
    position: relative;

    #productMapChart {
        height: 100%;
        height: 130px;

        div {
            height: 100%;
        }
    }

    // .legend {
    //     position: absolute;
    //     top: 20px;
    //     right: 20px;

    //     .average {
    //         .indicator {
    //             background-color: vars.$castleMoat;
    //             width: 4px;
    //             height: 20px;
    //         }
    //     }

    //     .similar,
    //     .target {
    //         .indicator {
    //             background-color: vars.$dungeonDepths;
    //             width: 7px;
    //             height: 7px;
    //             border-radius: 50%;
    //         }
    //     }

    //     .target {
    //         .indicator {
    //             background-color: vars.$gold;
    //             width: 14px;
    //             height: 14px;
    //         }
    //     }
    // }
}
</style>
